import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Cpu, Loader2, Shield } from 'lucide-react';
import { API_URL } from '../../config';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import SuperAdminShell from './SuperAdminShell';

export default function SuperAdminPlatformPage() {
  const [aiLoading, setAiLoading] = useState(true);
  const [aiSaving, setAiSaving] = useState(false);
  const [providersConfigured, setProvidersConfigured] = useState({});
  const [aiForm, setAiForm] = useState({
    active_provider: 'groq',
    model_groq: '',
    model_cloudflare: '',
    model_huggingface: '',
  });

  useEffect(() => {
    let cancelled = false;
    setAiLoading(true);
    axios
      .get(`${API_URL}/platform/ai-settings`)
      .then((res) => {
        if (cancelled) return;
        setAiForm({
          active_provider: res.data.active_provider || 'groq',
          model_groq: res.data.model_groq || '',
          model_cloudflare: res.data.model_cloudflare || '',
          model_huggingface: res.data.model_huggingface || '',
        });
        setProvidersConfigured(res.data.providers_configured || {});
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load AI platform settings');
      })
      .finally(() => {
        if (!cancelled) setAiLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const saveAiSettings = async () => {
    setAiSaving(true);
    try {
      const res = await axios.patch(`${API_URL}/platform/ai-settings`, {
        active_provider: aiForm.active_provider,
        model_groq: aiForm.model_groq,
        model_cloudflare: aiForm.model_cloudflare,
        model_huggingface: aiForm.model_huggingface,
      });
      setProvidersConfigured(res.data.providers_configured || {});
      toast.success('AI settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save AI settings');
    } finally {
      setAiSaving(false);
    }
  };

  const cfgBadge = (ok) => (
    <Badge variant={ok ? 'default' : 'secondary'} className={ok ? 'bg-emerald-600' : ''}>
      {ok ? 'API key OK' : 'Not configured'}
    </Badge>
  );

  return (
    <SuperAdminShell
      title="Platform"
      description="Operator-only settings for AI providers and access control."
      testId="super-admin-platform"
    >
      <div className="space-y-6 max-w-2xl">
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="font-[Manrope] flex items-center gap-2">
              <Cpu className="w-5 h-5 text-violet-600" />
              AI documentation assistant
            </CardTitle>
            <CardDescription>
              Active provider and model IDs for workspaces with the add-on enabled. Put API keys in server env
              (see docs/AI_ASSISTANT_ADDON.md).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {aiLoading ? (
              <div className="flex items-center gap-2 text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading…
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-2 text-xs items-center">
                  <span className="text-slate-500">Providers:</span>
                  {cfgBadge(providersConfigured.groq)}
                  {cfgBadge(providersConfigured.cloudflare)}
                  {cfgBadge(providersConfigured.huggingface)}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="active-provider">Active provider</Label>
                  <select
                    id="active-provider"
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                    value={aiForm.active_provider}
                    onChange={(e) => setAiForm((f) => ({ ...f, active_provider: e.target.value }))}
                    data-testid="super-admin-ai-provider"
                  >
                    <option value="groq">Groq</option>
                    <option value="cloudflare">Cloudflare Workers AI</option>
                    <option value="huggingface">Hugging Face</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="model-groq">Groq model id</Label>
                  <Input
                    id="model-groq"
                    value={aiForm.model_groq}
                    onChange={(e) => setAiForm((f) => ({ ...f, model_groq: e.target.value }))}
                    placeholder="llama-3.3-70b-versatile"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="model-cf">Cloudflare model</Label>
                  <Input
                    id="model-cf"
                    value={aiForm.model_cloudflare}
                    onChange={(e) => setAiForm((f) => ({ ...f, model_cloudflare: e.target.value }))}
                    placeholder="@cf/mistral/mistral-7b-instruct-v0.2"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="model-hf">Hugging Face model id</Label>
                  <Input
                    id="model-hf"
                    value={aiForm.model_huggingface}
                    onChange={(e) => setAiForm((f) => ({ ...f, model_huggingface: e.target.value }))}
                  />
                </div>
                <Button onClick={saveAiSettings} disabled={aiSaving} data-testid="super-admin-ai-save">
                  {aiSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save AI settings
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="font-[Manrope] flex items-center gap-2">
              <Shield className="w-5 h-5 text-violet-600" />
              Super admins
            </CardTitle>
            <CardDescription>
              Set <code className="text-xs bg-slate-100 dark:bg-slate-800 px-1 rounded">SUPER_ADMIN_EMAILS</code>{' '}
              on the API (comma-separated emails). Restart the server after changes.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    </SuperAdminShell>
  );
}
